// PROD-134 gamma (P6 PROD-134d): the ONE patient-side reader of the after-visit summary.
//
// The API stores one versioned AVS per case in `cases.avs` (`{text, version, source,
// encounterNo, ...}`, API _avs_read / _avs_store) and, for a patient, only a RELEASED one
// (PROD-517). This reader takes that stored text instead of re-deriving it from the legacy
// columns, and falls back to today's chain when a case has no stored object (older cases,
// an old API). It makes no request and logs nothing: the text is clinical content.
//
// readAvsText(c, activeEncounterNo):
//   1. c.avs.text when c.avs.encounterNo is the active encounter (or names none, or no
//      active encounter is known);
//   2. else that encounter's OWN after_visit_summary, never another encounter's;
//   3. else today's case-level chain, unwrapped.
// The text never starts with '{': the finalize envelope ({avsContent, consultNote,
// billingCodes, ...}) and the old AI writer's {content} are unwrapped, so a billing code
// can never reach a screen or a PDF through here. Shared fixture: the API's
// tools/avs_reader_fixtures.json (scripts/check-avs-text-reader.mjs).

const SOURCES = ['ai', 'physician', 'template']

// The text a legacy value carries, or ''. Mirrors API _avs_text_from_legacy.
export function unwrapAvs(v) {
  for (let i = 0; i < 3; i++) {
    if (typeof v === 'string') {
      const s = v.trim()
      if (!s.startsWith('{')) return s
      try { v = JSON.parse(s) } catch { return '' }
    }
    if (!v || typeof v !== 'object') return ''
    let inner = v.avsContent
    if (!(typeof inner === 'string' && inner.trim())) inner = v.content
    if (!(typeof inner === 'string' && inner.trim())) inner = v.text
    if (!(typeof inner === 'string' && inner.trim())) return ''
    v = inner
  }
  return ''
}

// A stored cases.avs object (or its JSON string) the API wrote, else null.
export function storedAvs(c) {
  let s = c?.avs
  if (typeof s === 'string') { try { s = JSON.parse(s) } catch { return null } }
  if (!s || typeof s !== 'object') return null
  const ok = typeof s.text === 'string' && s.text.trim() && !s.text.trimStart().startsWith('{')
    && Number.isInteger(s.version) && s.version >= 1 && SOURCES.includes(s.source)
  return ok ? s : null
}

const encNo = (e) => e?.encounterNumber ?? e?.encounter_number

export function readAvsText(c, activeEncounterNo) {
  if (!c || typeof c !== 'object') return ''
  const n = Number.isInteger(activeEncounterNo) ? activeEncounterNo : null
  const stored = storedAvs(c)
  const sNo = stored?.encounterNo ?? stored?.encounter_no
  if (stored && (n == null || !Number.isInteger(sNo) || sNo === n)) return stored.text
  if (n != null && Array.isArray(c.encounters)) {
    const enc = c.encounters.find((e) => encNo(e) === n)
    const own = unwrapAvs(enc?.afterVisitSummary ?? enc?.after_visit_summary)
    if (own) return own
  }
  let dd = c.diagnose_data ?? c.diagnoseData
  if (typeof dd === 'string') { try { dd = JSON.parse(dd) } catch { dd = null } }
  const chain = [
    n != null && n > 1 ? c.diagnose?.followUpAVS : null,
    c.avsContent, c.afterVisitSummary, c.after_visit_summary, c.diagnose?.avsContent, dd?.avsContent,
  ]
  for (const v of chain) { const t = unwrapAvs(v); if (t) return t }
  return ''
}
